import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { Send, CheckCircle2, AlertCircle } from "lucide-react";
import PageHero from "@/components/PageHero";

const Field = ({ label, name, value, onChange, required, type = "text", textarea, placeholder }) => (
  <label className="flex flex-col gap-1">
    <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">
      {label}{required ? " *" : ""}
    </span>
    {textarea ? (
      <textarea
        data-testid={`field-${name}`}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        rows={5}
        placeholder={placeholder}
        className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
      />
    ) : (
      <input
        data-testid={`field-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
      />
    )}
  </label>
);

const SubmitNews = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    submitter_name: "", submitter_email: "", submitter_company: "",
    title: "", summary: "", url: "", source: "", why_matters: "", tags: "",
    language: lang, website: "",
  });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setStatus(null);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      await api.post("/submit-news", payload);
      setStatus("ok");
      setForm({ submitter_name: "", submitter_email: "", submitter_company: "", title: "", summary: "", url: "", source: "", why_matters: "", tags: "", language: lang, website: "" });
    } catch {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  };

  const labels = lang === "fr" ? {
    title: "Soumettre une news",
    intro: "Vous avez repéré un signal IA pertinent pour les Opérations ? Partagez-le. Notre équipe éditoriale revoit chaque soumission avant publication.",
    you: "Vous",
    name: "Nom complet", email: "Email", company: "Entreprise",
    news: "La news",
    nTitle: "Titre", nSummary: "Résumé court", nUrl: "Lien source", nSource: "Nom de la source",
    nWhy: "Pourquoi cela compte pour les Opérations ?", nTags: "Tags (séparés par des virgules)",
    submit: "Envoyer la news", sending: "Envoi…",
    ok: "Merci ! Nous avons reçu votre soumission. L'équipe Dataleo la revoit avant publication.",
    err: "Erreur lors de l'envoi. Vérifiez les champs obligatoires.",
  } : {
    title: "Submit a news item",
    intro: "Spotted an AI signal that matters for Operations? Share it. Our editorial team reviews every submission before publication.",
    you: "You",
    name: "Full name", email: "Email", company: "Company",
    news: "The news",
    nTitle: "Title", nSummary: "Short summary", nUrl: "Source URL", nSource: "Source name",
    nWhy: "Why this matters for Operations?", nTags: "Tags (comma-separated)",
    submit: "Submit news", sending: "Sending…",
    ok: "Thanks! We received your submission. The Dataleo team will review it before publication.",
    err: "Something went wrong. Please check required fields.",
  };

  return (
    <div data-testid="submit-news-page">
      <Helmet>
        <title>{`${labels.title} - Dataleo Radar`}</title>
        <meta name="description" content={labels.intro} />
      </Helmet>
      <PageHero
        kicker="Contribute · Submit"
        title={labels.title}
        description={labels.intro}
        accent="green"
        compact
        testId="submit-hero"
      />
      <div className="dl-container py-10 max-w-3xl">

      <form onSubmit={submit} className="space-y-6">
        <div>
          <h2 className="font-display text-lg text-[#111827] mb-3">{labels.you}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={labels.name} name="submitter_name" value={form.submitter_name} onChange={set("submitter_name")} required />
            <Field label={labels.email} name="submitter_email" type="email" value={form.submitter_email} onChange={set("submitter_email")} required />
            <div className="sm:col-span-2">
              <Field label={labels.company} name="submitter_company" value={form.submitter_company} onChange={set("submitter_company")} />
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-display text-lg text-[#111827] mb-3">{labels.news}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Field label={labels.nTitle} name="title" value={form.title} onChange={set("title")} required />
            </div>
            <div className="sm:col-span-2">
              <Field label={labels.nSummary} name="summary" textarea value={form.summary} onChange={set("summary")} />
            </div>
            <Field label={labels.nUrl} name="url" type="url" value={form.url} onChange={set("url")} placeholder="https://" />
            <Field label={labels.nSource} name="source" value={form.source} onChange={set("source")} />
            <div className="sm:col-span-2">
              <Field label={labels.nWhy} name="why_matters" textarea value={form.why_matters} onChange={set("why_matters")} />
            </div>
            <div className="sm:col-span-2">
              <Field label={labels.nTags} name="tags" value={form.tags} onChange={set("tags")} placeholder="AI, Supply Chain, …" />
            </div>
          </div>
        </div>

        {/* honeypot */}
        <input type="text" name="website" value={form.website} onChange={(e) => set("website")(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" />

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={busy}
            data-testid="submit-news-btn"
            className="inline-flex items-center gap-2 bg-[#FFD400] hover:bg-[#facc15] disabled:opacity-60 text-[#111827] px-5 py-3 text-sm font-bold"
          >
            <Send size={14} /> {busy ? labels.sending : labels.submit}
          </button>
          {status === "ok" && (
            <div data-testid="submit-news-ok" className="inline-flex items-center gap-2 text-sm text-[#00A878]">
              <CheckCircle2 size={16} /> {labels.ok}
            </div>
          )}
          {status === "error" && (
            <div data-testid="submit-news-error" className="inline-flex items-center gap-2 text-sm text-red-700">
              <AlertCircle size={16} /> {labels.err}
            </div>
          )}
        </div>
      </form>
      </div>
    </div>
  );
};

export default SubmitNews;
